import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getUserManager } from '@/auth/oidc';
import { takeReturnTo } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { FullPageMessage } from '@/components/FullPageMessage';

/**
 * The authorization code hop back from Cognito Managed Login.
 *
 * This route sits outside `RequireAuth` on purpose: it is the thing that
 * produces the session, so gating it on having one would loop.
 *
 * The `ran` ref is not defensive decoration. React 18 and later mount effects
 * twice in development StrictMode, and the authorization code is single use, so
 * the second call would exchange an already consumed code and land the user on
 * an error screen that never appears in production. Guarding the call is the
 * only way to make development behave like production here.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const ran = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    void (async () => {
      try {
        await getUserManager().signinRedirectCallback(window.location.href);
        const target = takeReturnTo();
        // `replace` so the back button does not return to a callback URL whose
        // code has already been redeemed.
        void navigate(target && target.startsWith('/') ? target : '/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [navigate]);

  if (error !== null) {
    return (
      <FullPageMessage
        tone="destructive"
        title="Sign in did not complete"
        detail={`${error} This usually means the link was opened twice or the request expired.`}
      >
        <Button onClick={() => window.location.assign('/')}>Start again</Button>
      </FullPageMessage>
    );
  }

  return <FullPageMessage title="Signing you in" detail="One moment." busy />;
}
